import { onMounted, reactive, ref, watch } from 'vue';
import { router } from '@inertiajs/vue3';
import { SETTINGS_ALL_SHOPS_QUERY_VALUE } from '@/ShopManagement/settings.js';

export const ALL_SHOPS_SCOPE_ID = 'all-shops';

const allShopsScope = () => ({
  id: ALL_SHOPS_SCOPE_ID,
  name: 'MSO Manager',
  shortName: 'MSO Manager',
  type: 'multi',
  isAllShops: true,
});

export const buildShopScope = (shop) => {
  if (!shop?.shop_id) {
    return null;
  }

  return {
    id: `shop-${shop.shop_id}`,
    shopId: Number(shop.shop_id),
    name: shop.shop_name || shop.public_name || 'Current Shop',
    shortName: shop.shop_name || shop.public_name || 'Current Shop',
    ...Object.fromEntries(['currency', 'locale', 'currencyValid', 'money'].filter(key => Object.hasOwn(shop, key)).map(key => [key, shop[key]])),
    // Keep supplied financial context distinct from legacy display defaults.
    // Missing context must never establish a currency for an intershop transfer.
    ...(Object.hasOwn(shop, 'currency') ? { currency: shop.currency, currencyValid: shop.currencyValid !== false && typeof shop.currency === 'string' && Boolean(shop.currency.trim()) } : {}),
    ...(Object.hasOwn(shop, 'locale') ? { locale: shop.locale } : {}),
    ...((shop.inventoryCostingMethod ?? shop.inventory_costing_method) != null ? { inventoryCostingMethod: shop.inventoryCostingMethod ?? shop.inventory_costing_method } : {}),
    documentIdentity: {
      name: shop.public_name || shop.shop_name || 'Current Shop',
      address: shop.address || null,
      logoUrl: shop.image_url || shop.logo || '',
      phone: shop.shop_phone_number || '',
      email: shop.shop_email || '',
    },
    city: shop.address?.city || '',
    state: shop.address?.state || '',
    isMsoMaster: Boolean(shop.mso_master_shop),
    type: 'shop',
    isAllShops: false,
  };
};

export const useShopManagementScope = (page) => {
  const initialShop = buildShopScope(page.props.master_config);
  const shopScopes = ref(initialShop ? [initialShop] : []);

  const requestedScope = () => {
    if (typeof window === 'undefined') {
      return null;
    }

    return new URLSearchParams(window.location.search).get('scope');
  };

  const resolveInitialScope = () => {
    if (requestedScope() === SETTINGS_ALL_SHOPS_QUERY_VALUE) {
      return shopScopes.value.find((scope) => scope.isAllShops) || shopScopes.value[0] || null;
    }

    return (
      shopScopes.value.find(
        (scope) => Number(scope.shopId) === Number(page.props.master_config?.shop_id),
      ) ||
      shopScopes.value.find((scope) => !scope.isAllShops) ||
      shopScopes.value[0] ||
      null
    );
  };

  const initialScope = resolveInitialScope();
  const scopeContext = reactive({
    selectedScope: initialScope,
    selectedScopeId: initialScope?.id || ALL_SHOPS_SCOPE_ID,
    isAllShops: Boolean(initialScope?.isAllShops),
    scopes: shopScopes.value,
    setScope: null,
  });

  const ensureCurrentShopScope = (masterConfig = page.props.master_config) => {
    const normalizedScope = buildShopScope(masterConfig);

    if (!normalizedScope) {
      return null;
    }

    const existingScope = shopScopes.value.find(
      (scope) => Number(scope.shopId) === Number(normalizedScope.shopId),
    );

    if (existingScope) {
      Object.assign(existingScope, normalizedScope);
      return existingScope;
    }

    const firstShopIndex = shopScopes.value.findIndex((scope) => !scope.isAllShops);
    shopScopes.value.splice(
      firstShopIndex < 0 ? shopScopes.value.length : firstShopIndex,
      0,
      normalizedScope,
    );

    return normalizedScope;
  };

  const resolveScopeFromPage = () => {
    const masterConfig = page.props.master_config || {};

    if (requestedScope() === SETTINGS_ALL_SHOPS_QUERY_VALUE) {
      return shopScopes.value.find((scope) => scope.isAllShops) || shopScopes.value[0] || null;
    }

    return (
      ensureCurrentShopScope(masterConfig) ||
      shopScopes.value.find((scope) => Number(scope.shopId) === Number(masterConfig.shop_id)) ||
      shopScopes.value.find((scope) => !scope.isAllShops) ||
      shopScopes.value[0] ||
      null
    );
  };

  const syncScopeContextFromPage = () => {
    const resolvedScope = resolveScopeFromPage();

    if (!resolvedScope) {
      return;
    }

    scopeContext.selectedScope = resolvedScope;
    scopeContext.selectedScopeId = resolvedScope.id;
    scopeContext.isAllShops = Boolean(resolvedScope.isAllShops);
    scopeContext.scopes = shopScopes.value;
  };

  const syncScopeQuery = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);

    if (scopeContext.isAllShops) {
      url.searchParams.set('scope', SETTINGS_ALL_SHOPS_QUERY_VALUE);
    } else {
      url.searchParams.delete('scope');
    }

    router.replace({ url: `${url.pathname}${url.search}${url.hash}`, preserveState: true, preserveScroll: true });
  };

  const setScope = (scope) => {
    if (!scope) {
      return;
    }

    scopeContext.selectedScope = scope;
    scopeContext.selectedScopeId = scope.id;
    scopeContext.isAllShops = Boolean(scope.isAllShops);
    syncScopeQuery();
  };

  const getMsoShops = async () => {
    if (page.props.adminContext) return;
    try {
      const response = await axios.get(
        route('shop-management.mso-user-shops', {
          shop_id: page.props.master_config.shop_id,
        }),
      );
      const shops = response?.data?.data?.shops;
      if (page.props.adminContext) return;
      const normalizedShops = Array.isArray(shops) ? shops.map(buildShopScope).filter(Boolean) : [];

      const msoAccess = response?.data?.data?.msoAccess === true;

      shopScopes.value =
        msoAccess && Array.isArray(shops) && shops.length > 1
          ? [allShopsScope(), ...normalizedShops]
          : normalizedShops;
    } catch (error) {
      shopScopes.value = [];
    }

    ensureCurrentShopScope(page.props.master_config);
    syncScopeContextFromPage();
  };

  scopeContext.setScope = setScope;
  syncScopeContextFromPage();

  watch(() => page.url, syncScopeContextFromPage);
  watch(() => page.props.master_config, syncScopeContextFromPage, { deep: true });

  onMounted(getMsoShops);
  watch(
    () => Boolean(page.props.adminContext),
    (admin, wasAdmin) => {
      if (!admin && wasAdmin) getMsoShops();
    },
  );

  return {
    shopScopes,
    scopeContext,
    setScope,
    syncScopeContextFromPage,
  };
};
